import Konva from "konva";
import { useRef } from "react";
import { Point } from "frontend/utils/math-utils";
import WalkthroughTipPresenter from "frontend/walkthrough-tips/walkthrough-tip-presenter";
import { WalkthroughTipName } from "frontend/walkthrough-tips/walkthrough-tip-model";
import { CimMinusIcon, MinusIcon } from "frontend/icons/minus";
import { CimPlusZoomIcon, PlusZoomIcon } from "frontend/icons/plus-zoom";
import ZoomInput from "./zoom-input";
import { FooterButtonGroup, FooterButton } from "../footer";
import { useIsCIMReorderEnabled } from "utils/url-utils";
import styles from "./zoom-controls.module.css";

const STOPS = [0.01, 0.02, 0.03, 0.05, 0.1, 0.15, 0.2, 0.33, 0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3, 4];
const STOPS_REVERSE = [...STOPS].reverse();

function stop(scale: number, stops: number[], comparator: (a: number, b: number) => boolean) {
  const index = stops.findIndex((element) => comparator(scale, element));
  return index > -1 ? stops[index] : scale;
}

function nextStop(scale: number) {
  return stop(scale, STOPS, (a, b) => a < b);
}
function previousStop(scale: number) {
  return stop(scale, STOPS_REVERSE, (a, b) => a > b);
}

export const useZoomControls = ({
  position,
  scale,
  onZoom,
  viewportWidth,
  viewportHeight,
}: {
  position: Point;
  scale: number;
  onZoom: (scale: number, position: Point) => void;
  viewportWidth?: number;
  viewportHeight?: number;
}) => {
  const ref = useRef(null);

  function onScaleChange(scale: number, newScale: number) {
    const centerX = (0.5 * (viewportWidth || window.innerWidth) - position.x) / scale;
    const centerY = (0.5 * (viewportHeight || window.innerHeight) - position.y) / scale;
    const offsetX = centerX * (newScale - scale);
    const offsetY = centerY * (newScale - scale);
    const newPosition = {
      x: position.x - offsetX,
      y: position.y - offsetY,
    };
    onZoom(newScale, newPosition);
  }

  const zoomOut = () => {
    onScaleChange(scale, previousStop(scale));
  };
  const zoomIn = () => {
    onScaleChange(scale, nextStop(scale));
  };

  return {
    ref,
    onScaleChange,
    zoomOut,
    zoomIn,
  };
};

export function ZoomControls({
  position,
  scale,
  onZoom,
  viewportWidth,
  viewportHeight,
  showTooltips = true,
  tipName,
  onTipActionClick,
  stageRef,
}: {
  position: Point;
  scale: number;
  onZoom: (scale: number, position: Point) => void;
  viewportWidth?: number;
  viewportHeight?: number;
  showTooltips?: boolean;
  tipName?: WalkthroughTipName;
  onTipActionClick?: () => void;
  stageRef?: React.RefObject<Konva.Stage>;
}) {
  const { ref, onScaleChange, zoomOut, zoomIn } = useZoomControls({
    position,
    scale,
    onZoom,
    viewportWidth,
    viewportHeight,
  });

  const isCimReorderEnabled = useIsCIMReorderEnabled();

  const ZoomOutButton = isCimReorderEnabled ? (
    <div data-testid="zoom-out-button" className={styles.cimZoomButton} onClick={zoomOut}>
      <div className={styles.cimZoomButtonContent}>
        <CimMinusIcon />
      </div>
    </div>
  ) : (
    <FooterButton testid="zoom-out-button" tooltip={showTooltips ? "Zoom out" : undefined} onClick={zoomOut}>
      <MinusIcon />
    </FooterButton>
  );
  const ZoomInButton = isCimReorderEnabled ? (
    <div data-testid="zoom-in-button" className={styles.cimZoomButton} onClick={zoomIn} ref={ref}>
      <div className={styles.cimZoomButtonContent}>
        <CimPlusZoomIcon />
      </div>
    </div>
  ) : (
    <FooterButton testid="zoom-in-button" tooltip={showTooltips ? "Zoom in" : undefined} onClick={zoomIn} ref={ref}>
      <PlusZoomIcon />
    </FooterButton>
  );

  if (isCimReorderEnabled) {
    return (
      <div className={styles.cimZoomControls}>
        {ZoomOutButton}
        <ZoomInput scale={scale} onScaleChange={onScaleChange} onZoom={onZoom} stageRef={stageRef} />
        {ZoomInButton}
      </div>
    );
  }

  return (
    <>
      <FooterButtonGroup>
        {ZoomOutButton}
        <ZoomInput scale={scale} onScaleChange={onScaleChange} onZoom={onZoom} stageRef={stageRef} />
        {ZoomInButton}
      </FooterButtonGroup>
      {showTooltips && tipName && (
        <WalkthroughTipPresenter
          tipName={tipName}
          attachToRef={ref}
          side="top"
          onActionClick={onTipActionClick}
          showArrow={false}
          headerTitle={"WHAT'S NEW?"}
          showSkipButton={false}
        />
      )}
    </>
  );
}
